import { useMemo, useState } from 'react';
import { allgemeinstromUmlage, berechneRendite, MIETERSTROM_HINWEIS } from '@nkp/shared';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { formatEuro, parseEuroEingabe } from '../lib/format';

/** Euro-Eingabe → Cent, leer/ungültig = 0. */
function euro(v: string): number {
  return parseEuroEingabe(v) ?? 0;
}
/** Prozent-Eingabe → Zahl, Komma erlaubt. */
function proz(v: string): number {
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

export function Rechner() {
  // Neutrale Startwerte, damit sofort ein Ergebnis sichtbar ist (reiner Rechner, keine Beispieldaten).
  const [kaufpreis, setKaufpreis] = useState('400.000');
  const [grest, setGrest] = useState('5,0');
  const [notar, setNotar] = useState('1,5');
  const [makler, setMakler] = useState('3,57');
  const [eigenkapital, setEigenkapital] = useState('100.000');
  const [zins, setZins] = useState('3,5');
  const [tilgung, setTilgung] = useState('2,0');
  const [jahresmiete, setJahresmiete] = useState('16.000');
  const [bewirtschaftung, setBewirtschaftung] = useState('2.400');

  const r = useMemo(() => {
    const kaufpreisCent = euro(kaufpreis);
    if (kaufpreisCent <= 0) return null;
    return berechneRendite({
      kaufpreisCent,
      grunderwerbsteuerProzent: proz(grest),
      notarProzent: proz(notar),
      maklerProzent: proz(makler),
      eigenkapitalCent: euro(eigenkapital),
      sollzinsProzent: proz(zins),
      tilgungProzent: proz(tilgung),
      jahresKaltmieteCent: euro(jahresmiete),
      bewirtschaftungskostenCent: euro(bewirtschaftung),
    });
  }, [kaufpreis, grest, notar, makler, eigenkapital, zins, tilgung, jahresmiete, bewirtschaftung]);

  const pos = (r?.cashflowVorSteuerCent ?? 0) >= 0;

  return (
    <div>
      <Seitenkopf
        titel="Kauf- und Renditerechner"
        beschreibung="Bewertung eines Kaufs vor dem Erwerb: Brutto- und Nettomietrendite, Kapitaldienst, Cashflow und Eigenkapitalrendite. Vor-steuerlich, ohne Abschreibung."
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Eingaben */}
        <div className="space-y-5">
          <Card titel="Kauf">
            <div className="space-y-4">
              <Input label="Kaufpreis (€)" inputMode="decimal" value={kaufpreis} onChange={(e) => setKaufpreis(e.target.value)} />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Input label="Grunderwerbst. (%)" inputMode="decimal" value={grest} onChange={(e) => setGrest(e.target.value)} />
                <Input label="Notar/Grundbuch (%)" inputMode="decimal" value={notar} onChange={(e) => setNotar(e.target.value)} />
                <Input label="Makler (%)" inputMode="decimal" value={makler} onChange={(e) => setMakler(e.target.value)} />
              </div>
            </div>
          </Card>

          <Card titel="Finanzierung">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input label="Eigenkapital (€)" inputMode="decimal" value={eigenkapital} onChange={(e) => setEigenkapital(e.target.value)} />
              <Input label="Sollzins (% p. a.)" inputMode="decimal" value={zins} onChange={(e) => setZins(e.target.value)} />
              <Input label="Tilgung (% p. a.)" inputMode="decimal" value={tilgung} onChange={(e) => setTilgung(e.target.value)} />
            </div>
          </Card>

          <Card titel="Ertrag">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label="Jahreskaltmiete (€)" inputMode="decimal" value={jahresmiete} onChange={(e) => setJahresmiete(e.target.value)} />
              <Input label="Bewirtschaftung / Jahr (€)" inputMode="decimal" value={bewirtschaftung} onChange={(e) => setBewirtschaftung(e.target.value)} />
            </div>
            <p className="mt-2 text-xs text-fg-subtle">
              Bewirtschaftung = nicht umlagefähige Kosten (Verwaltung, Instandhaltungsrücklage,
              Mietausfallwagnis).
            </p>
          </Card>
        </div>

        {/* Ergebnis */}
        <div className="space-y-5">
          {!r ? (
            <Card>
              <p className="text-sm text-fg-muted">Bitte einen Kaufpreis größer null eingeben.</p>
            </Card>
          ) : (
            <>
              <Card>
                <div className="grid grid-cols-2 gap-3">
                  <Gross label="Bruttomietrendite" wert={`${r.bruttomietrenditeProzent.toLocaleString('de-DE')} %`} />
                  <Gross label="Nettomietrendite" wert={`${r.nettomietrenditeProzent.toLocaleString('de-DE')} %`} betont />
                  <Gross label="Eigenkapitalrendite" wert={`${r.eigenkapitalrenditeProzent.toLocaleString('de-DE')} %`} />
                  <Gross label="Kaufpreisfaktor" wert={`${r.kaufpreisfaktor.toLocaleString('de-DE')}×`} />
                </div>
              </Card>

              <Card titel="Cashflow (nach Zins und Tilgung)">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-fg-muted">monatlich</span>
                  <span className={`tabellenziffern text-2xl font-bold ${pos ? 'text-success' : 'text-danger'}`}>
                    {formatEuro(r.cashflowMonatlichCent)}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-sm text-fg-muted">jährlich</span>
                  <span className={`tabellenziffern text-sm font-semibold ${pos ? 'text-success' : 'text-danger'}`}>
                    {formatEuro(r.cashflowVorSteuerCent)}
                  </span>
                </div>
                <div className="mt-2">
                  {pos ? (
                    <Badge ton="erfolg">Trägt sich selbst</Badge>
                  ) : (
                    <Badge ton="warnung">Zuzahlung nötig</Badge>
                  )}
                </div>
              </Card>

              <Card titel="Zusammensetzung">
                <Zeile label="Kaufnebenkosten" wert={formatEuro(r.kaufnebenkostenCent)} />
                <Zeile label="Gesamtinvestition" wert={formatEuro(r.gesamtinvestitionCent)} betont />
                <Zeile label="Darlehen" wert={formatEuro(r.darlehenCent)} />
                <div className="my-1 border-t border-border" />
                <Zeile label="Jahresreinertrag (Miete − Bewirtschaftung)" wert={formatEuro(r.jahresreinertragCent)} />
                <Zeile label="Zins p. a." wert={formatEuro(r.jahreszinsCent)} />
                <Zeile label="Tilgung p. a." wert={formatEuro(r.jahrestilgungCent)} />
                <Zeile label="Kapitaldienst p. a." wert={formatEuro(r.kapitaldienstCent)} betont />
              </Card>

              <p className="px-1 text-xs text-fg-subtle">
                Vor-steuerliche Betrachtung ohne AfA, Sonderabschreibung und persönlichen Steuersatz.
                Die Tilgung ist Vermögensaufbau, keine Kostenposition — ein negativer Cashflow bei
                hoher Tilgung bedeutet nicht automatisch ein schlechtes Investment. Keine
                Anlageberatung.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="mt-8">
        <PvRechner />
      </div>
    </div>
  );
}

/** Zahl-Eingabe (kWh, ct) → Zahl, Komma erlaubt, leer/ungültig = 0. */
function num(v: string): number {
  const n = Number(v.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function PvRechner() {
  const [externe, setExterne] = useState('4.200');
  const [eigenverbrauch, setEigenverbrauch] = useState('1.200');
  const [arbeitspreis, setArbeitspreis] = useState('30');

  const r = useMemo(
    () =>
      allgemeinstromUmlage({
        externeStromkostenCent: Math.round(num(externe) * 100),
        pvEigenverbrauchKwh: num(eigenverbrauch),
        arbeitspreisCentProKwh: Math.round(num(arbeitspreis)),
      }),
    [externe, eigenverbrauch, arbeitspreis],
  );

  return (
    <div>
      <Seitenkopf
        titel="Photovoltaik & Mieterstrom"
        beschreibung="Wie viel Allgemeinstrom ist bei eigener PV-Anlage umlagefähig? Betriebskosten sind nur die tatsächlich bezahlten Fremdstromkosten — selbst erzeugter PV-Strom wird nicht auf die Mietparteien umgelegt."
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card titel="Allgemeinstrom">
          <div className="space-y-4">
            <Input
              label="Fremdstromrechnung Allgemeinstrom / Jahr (€)"
              inputMode="decimal"
              value={externe}
              onChange={(e) => setExterne(e.target.value)}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="PV-Eigenverbrauch (kWh)"
                inputMode="decimal"
                value={eigenverbrauch}
                onChange={(e) => setEigenverbrauch(e.target.value)}
              />
              <Input
                label="Arbeitspreis (ct/kWh)"
                inputMode="decimal"
                value={arbeitspreis}
                onChange={(e) => setArbeitspreis(e.target.value)}
              />
            </div>
            <p className="text-xs text-fg-subtle">
              PV-Eigenverbrauch und Arbeitspreis dienen nur der Transparenz (eingesparte Fremdkosten).
              Sie erhöhen die Umlage nicht.
            </p>
          </div>
        </Card>

        <div className="space-y-5">
          <Card>
            <div className="grid grid-cols-2 gap-3">
              <Gross label="Umlagefähig (nur Fremdstrom)" wert={formatEuro(r.umlagefaehigCent)} betont />
              <Gross label="PV-Ersparnis (nur Ausweis)" wert={formatEuro(r.ersparnisCent)} />
            </div>
            {r.hinweise.map((h, i) => (
              <p key={i} className="mt-3 rounded-lg bg-surface-2 px-3 py-2 text-xs text-fg-muted">
                {h}
              </p>
            ))}
          </Card>

          <Card titel="Mieterstrom">
            <div className="flex items-start gap-2">
              <Badge ton="warnung">Keine Betriebskosten</Badge>
            </div>
            <p className="mt-2 text-sm text-fg-muted">{MIETERSTROM_HINWEIS}</p>
          </Card>
        </div>
      </div>

      <p className="mt-4 px-1 text-xs text-fg-subtle">
        Rechtsstand 15.09.2026. Einordnung nach § 556 Abs. 1 BGB, § 1 BetrKV (tatsächliche Kosten)
        und EnWG (Mieterstrom als Stromliefervertrag). Keine Rechtsberatung — im Zweifel anwaltlich
        prüfen lassen.
      </p>
    </div>
  );
}

function Gross({ label, wert, betont }: { label: string; wert: string; betont?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${betont ? 'border-accent/40 bg-accent-soft' : 'border-border bg-surface-2'}`}>
      <p className="text-xs text-fg-muted">{label}</p>
      <p className={`mt-1 tabellenziffern text-xl font-bold ${betont ? 'text-accent-soft-fg' : 'text-fg'}`}>{wert}</p>
    </div>
  );
}

function Zeile({ label, wert, betont }: { label: string; wert: string; betont?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-sm text-fg-muted">{label}</span>
      <span className={`tabellenziffern text-sm ${betont ? 'font-semibold text-fg' : 'text-fg'}`}>{wert}</span>
    </div>
  );
}

export default Rechner;
